import React from 'react';
import PropTypes from 'prop-types';
import PageGuides from './PageGuides';

/**
 * PageCard Component
 * 
 * Renders an individual page visualization in the layout diagram.
 * Each card can represent either an actual page or a blank page,
 * and can display page numbers, fold guides, and hole punch guides.
 *
 * @component
 */
const PageCard = ({
  pageNumbers,     // Array of page numbers to display (can include blanks)
  isBlank = false,  // Default parameter instead of defaultProps
  orientation = 'portrait',
  showDebugNumbers = false,
  showGuides = true,
  showHoleGuides = true,
  showFoldLine = true,
  thumbnails = [],
  thumbnailTransforms = null,
  showPageImages = true,
  showOutputPageNumber = false,
  pageNumberOffset = 0,
  pageNumberFontSize = 12
}) => {
  const isEntirelyBlank = isBlank || pageNumbers.every((num) => {
    if (typeof num === 'number') {
      return num <= 0;
    }
    return !num;
  });

  const primaryThumbnail = thumbnails.find((thumb) => Boolean(thumb));
  const hasMultipleSlots = pageNumbers.length > 1;

  const normalizedThumbnails = orientation === 'landscape' && hasMultipleSlots
    ? [thumbnails[0] || null, thumbnails[1] || null]
    : [primaryThumbnail || null];

  const shouldUseTransforms = orientation === 'landscape'
    && hasMultipleSlots
    && thumbnailTransforms
    && thumbnailTransforms.left
    && thumbnailTransforms.right;

  const getAltLabel = (index = 0) => {
    const value = pageNumbers[index];
    if (typeof value === 'number' && value > 0) {
      return `Page ${value}`;
    }
    return 'Blank page';
  };

  return (
    <div
      className={`
        page-card
        ${orientation}
        ${isEntirelyBlank ? 'blank-page' : ''}
        ${!showPageImages ? 'no-thumbnails' : ''}
      `}
    >
      <div className={`thumbnail-layer ${shouldUseTransforms ? 'transformed' : ''}`}>
        {isEntirelyBlank ? (
          <div className="thumbnail-placeholder" />
        ) : orientation === 'landscape' && hasMultipleSlots ? (
          shouldUseTransforms ? (
            <div className="thumbnail-canvas">
              {['left', 'right'].map((side, idx) => {
                const thumb = normalizedThumbnails[idx];
                const sideStyle = thumbnailTransforms?.[side] || {};
                return thumb ? (
                  <div key={side} className="thumbnail-half transformed" style={sideStyle}>
                    <img
                      src={thumb}
                      alt={getAltLabel(idx)}
                      className="thumbnail-half-image"
                    />
                  </div>
                ) : (
                  <div
                    key={side}
                    className="thumbnail-half transformed placeholder"
                    style={sideStyle}
                  />
                );
              })}
            </div>
          ) : (
            <div className="thumbnail-split">
              {normalizedThumbnails.map((thumb, idx) => (
                thumb ? (
                  <div key={idx} className="thumbnail-half">
                    <img
                      src={thumb}
                      alt={getAltLabel(idx)}
                      className="thumbnail-half-image"
                    />
                  </div>
                ) : (
                  <div key={idx} className="thumbnail-half placeholder" />
                )
              ))}
            </div>
          )
        ) : primaryThumbnail ? (
          <img
            src={primaryThumbnail}
            alt={getAltLabel(0)}
            className="thumbnail-image"
          />
        ) : (
          <div className="thumbnail-placeholder" />
        )}
      </div>
      {/* Render guides if enabled */}
      {showGuides && (
        <PageGuides
          showFoldLine={showFoldLine}
          showHoleGuides={showHoleGuides}
        />
      )}
      {showDebugNumbers && (
        <div className="debug-number-overlay">
          {pageNumbers.map((num, idx) => (
            <span key={idx}>{typeof num === 'number' && num > 0 ? num : ''}</span>
          ))}
        </div>
      )}
      {showOutputPageNumber && pageNumbers.length === 2 && (
        <>
          {(() => {
            // Left page number (pageNumbers[0])
            const leftPageNum = typeof pageNumbers[0] === 'number' && pageNumbers[0] > 0 ? pageNumbers[0] : 0;
            if (leftPageNum > 0) {
              const displayNumber = leftPageNum - pageNumberOffset;
              if (displayNumber > 0) {
                return (
                  <div 
                    className="output-page-number bottom-left"
                    style={{ fontSize: `${pageNumberFontSize}px` }}
                  >
                    {displayNumber}
                  </div>
                );
              }
            }
            return null;
          })()}
          {(() => {
            // Right page number (pageNumbers[1])
            const rightPageNum = typeof pageNumbers[1] === 'number' && pageNumbers[1] > 0 ? pageNumbers[1] : 0;
            if (rightPageNum > 0) {
              const displayNumber = rightPageNum - pageNumberOffset;
              if (displayNumber > 0) {
                return (
                  <div 
                    className="output-page-number bottom-right"
                    style={{ fontSize: `${pageNumberFontSize}px` }}
                  >
                    {displayNumber}
                  </div>
                );
              }
            }
            return null;
          })()}
        </>
      )}
    </div>
  );
};

PageCard.propTypes = {
  pageNumbers: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ])).isRequired,
  isBlank: PropTypes.bool,
  orientation: PropTypes.string,
  showDebugNumbers: PropTypes.bool,
  showGuides: PropTypes.bool,
  showHoleGuides: PropTypes.bool,
  showFoldLine: PropTypes.bool,
  thumbnails: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.oneOf([null])
    ])
  ),
  thumbnailTransforms: PropTypes.shape({
    left: PropTypes.object,
    right: PropTypes.object,
  }),
  showPageImages: PropTypes.bool,
  showOutputPageNumber: PropTypes.bool,
  pageNumberOffset: PropTypes.number,
  pageNumberFontSize: PropTypes.number
};

export default PageCard; 